"use client";

import * as React from "react";
import Link from "next/link";
import Image from "next/image";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { LockIcon, ImageIcon, CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { pickLocalizedString } from "@/lib/locale";
import { resolveIcon } from "@/lib/icon-map";
import type { GalleryPageContent } from "@/types/pages";
import type { LocaleCode } from "@/types/sections";
import { buildGalleryDetailPath, getLocalizedPathname } from "@/lib/site";

import { db } from "@/lib/firebase.client";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  type Unsubscribe,
} from "firebase/firestore";

type EventType = "evenement public" | "evenement privee";
type GalleryContentItem = {
  id?: string | number;
  image: string;
  title?: string;
  description?: string;
};

type MediaItem = {
  id?: string | number;
  type: "image" | "video";
  url: string;
  title?: string;
  desc?: string;
  span?: string;
};

type GalleryDoc = {
  title: string;
  description: string;
  date: string;
  cover: string;
  type: EventType;
  accessKey?: string;
  content?: GalleryContentItem[];
  media?: MediaItem[];
  cards?: {
    id?: string;
    image: string;
    title: string;
    description: string;
  }[];
  isPublished?: boolean;
};

function canAccessPrivate(evt: GalleryDoc, key?: string) {
  if (evt.type !== "evenement privee") return true;
  const normalized = (key || "").trim();
  if (!normalized) return false;
  if (evt.accessKey && normalized === evt.accessKey) return true;
  return normalized === evt.title;
}

function extractPhotos(evt: GalleryDoc): GalleryContentItem[] {
  if (Array.isArray(evt.content) && evt.content.length > 0) {
    return evt.content.map((item, idx) => ({
      ...item,
      id: item.id ?? `content-${idx}`,
    }));
  }

  const legacyCards = Array.isArray(evt.cards) ? evt.cards : [];
  if (legacyCards.length > 0) {
    return legacyCards.map((card, idx) => ({
      id: card.id ?? `card-${idx}`,
      image: card.image,
      title: card.title,
      description: card.description,
    }));
  }

  const legacyMedia = Array.isArray(evt.media) ? evt.media : [];
  if (legacyMedia.length > 0) {
    return legacyMedia
      .filter((m) => m.type === "image")
      .map((m, idx) => ({
        id: m.id ?? `media-${idx}`,
        image: m.url,
        title: m.title,
        description: m.desc,
      }));
  }

  return [];
}

export default function GalleryPageClient({
  locale,
  content,
}: {
  locale: LocaleCode;
  content: GalleryPageContent;
}) {
  const [selectedType, setSelectedType] = React.useState<"all" | EventType>("all");
  const [search, setSearch] = React.useState("");
  const [events, setEvents] = React.useState<Array<GalleryDoc & { id: string }>>([]);
  const [loading, setLoading] = React.useState(true);
  const [unlocked, setUnlocked] = React.useState<Record<string, boolean>>({});

  React.useEffect(() => {
    let unsub: Unsubscribe | undefined;
    (async () => {
      try {
        const base = collection(db, "website-gallery");
        const q = query(base, orderBy("date", "desc"));
        unsub = onSnapshot(q, (snap) => {
          const arr: Array<GalleryDoc & { id: string }> = [];
          snap.forEach((d) =>
            arr.push({ id: d.id, ...(d.data() as GalleryDoc) })
          );
          const published = arr.filter((doc) => doc.isPublished !== false);
          setEvents(published);
          setLoading(false);
        });
      } catch {
        setLoading(false);
      }
    })();
    return () => unsub?.();
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const next: Record<string, boolean> = {};
    events.forEach((evt) => {
      if (evt.type !== "evenement privee") return;
      try {
        const stored = window.localStorage.getItem(`gallery-access-${evt.id}`);
        if (stored && canAccessPrivate(evt, stored)) {
          next[evt.id] = true;
        }
      } catch {
        // ignore
      }
    });
    setUnlocked(next);
  }, [events]);

  const filtered = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    return events.filter((e) => {
      const byType = selectedType === "all" ? true : e.type === selectedType;
      const bySearch =
        !term ||
        [e.title, e.description].some((v) =>
          (v || "").toLowerCase().includes(term)
        );
      return byType && bySearch;
    });
  }, [events, selectedType, search]);

  const heroTitle = pickLocalizedString(content.hero.title, locale, "Galerie");
  const heroSubtitle = pickLocalizedString(content.hero.subtitle, locale);
  const heroDescription = pickLocalizedString(content.hero.description, locale);
  const heroEyebrow = pickLocalizedString(content.hero.eyebrow, locale);
  const HeroIcon = resolveIcon(content.hero.iconKey, ImageIcon);

  const searchPlaceholder = pickLocalizedString(
    content.searchPlaceholder,
    locale,
    "Rechercher un événement…"
  );
  const filters = {
    all: pickLocalizedString(content.filters.all, locale, "Tous"),
    public: pickLocalizedString(content.filters.public, locale, "Événement public"),
    private: pickLocalizedString(content.filters.private, locale, "Événement privé"),
  };
  const loadingLabel = pickLocalizedString(content.loadingLabel, locale, "Chargement…");
  const badgePublic = pickLocalizedString(content.badges.public, locale, "Public");
  const badgePrivate = pickLocalizedString(content.badges.private, locale, "Privé");
  const buttonLabel = pickLocalizedString(content.viewButtonLabel, locale, "Voir");
  const photoSuffix = pickLocalizedString(content.photoCountSuffix, locale, "photos");
  const homeLabel = locale === "fr" ? "Accueil" : "Home";

  return (
    <div className="grid items-center justify-items-center min-h-screen font-[family-name:var(--font-geist-sans)]">
      <div className="flex flex-col row-start-2 items-center sm:items-start w-full overflow-x-hidden">
        <Breadcrumb className="mx-auto mt-[150px]">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link href={getLocalizedPathname(locale, "/")}>{homeLabel}</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{heroTitle}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="mx-auto max-w-6xl text-center mb-12 w-fit mt-12 px-4">
          <div className="flex justify-center mb-4">
            <div className="w-fit rounded-lg border border-[#BBD8E6] p-2 bg-white/80 dark:bg-zinc-900/80">
              <HeroIcon className="h-5 w-5 text-[#BBD8E6]" />
            </div>
          </div>
          {heroEyebrow && (
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">
              {heroEyebrow}
            </p>
          )}
          <h1 className="text-4xl font-bold text-black dark:text-white mb-4">{heroTitle}</h1>
          <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
            {heroSubtitle}
          </p>
          {heroDescription && (
            <p className="text-base text-neutral-500 dark:text-neutral-400 max-w-2xl mx-auto mt-4">
              {heroDescription}
            </p>
          )}
        </div>

        <div className="w-full max-w-6xl mx-auto px-4 mb-12">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-4"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedType === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedType("all")}
                className="text-sm capitalize"
              >
                {filters.all}
              </Button>
              <Button
                variant={selectedType === "evenement public" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedType("evenement public")}
                className="text-sm capitalize"
              >
                {filters.public}
              </Button>
              <Button
                variant={selectedType === "evenement privee" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedType("evenement privee")}
                className="text-sm capitalize"
              >
                {filters.private}
              </Button>
            </div>
          </div>
        </div>

        <div className="w-full max-w-6xl mx-auto px-4 mb-16">
          {loading ? (
            <div className="text-sm text-neutral-500">{loadingLabel}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((evt, index) => {
                const isPrivate = evt.type === "evenement privee";
                const hasAccess = isPrivate ? Boolean(unlocked[evt.id]) : true;
                const showLockedState = isPrivate && !hasAccess;
                const isAboveFoldImage = index < 3;
                const photos = extractPhotos(evt);
                const photoCount = photos.length;
                const photoLabel =
                  photoCount > 0 ? `${photoCount} ${photoSuffix}` : `0 ${photoSuffix}`;

                return (
                  <article key={evt.id} className="group cursor-pointer">
                    <Link href={buildGalleryDetailPath(locale, evt.id, evt.title)}>
                      <div className="bg-white dark:bg-neutral-900 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1 h-full">
                        <div className="aspect-video relative overflow-hidden">
                          <Image
                            src={evt.cover}
                            alt={evt.title}
                            width={500}
                            height={500}
                            sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                            loading={isAboveFoldImage ? "eager" : "lazy"}
                            fetchPriority={isAboveFoldImage ? "high" : "auto"}
                            className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${
                              showLockedState ? "opacity-0" : "opacity-100"
                            }`}
                          />
                          {showLockedState && (
                            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-black/60 to-black/40">
                              <div className="rounded-3xl border border-white/30 w-40 h-40 flex items-center justify-center">
                                <LockIcon className="w-10 h-10 text-white" />
                              </div>
                            </div>
                          )}
                          <div className="absolute left-3 top-3 flex gap-2">
                            <Badge
                              className={
                                isPrivate ? "bg-rose-200 text-black" : "bg-[#BBD8E6] text-black"
                              }
                            >
                              {isPrivate ? badgePrivate : badgePublic}
                            </Badge>
                          </div>
                        </div>

                        <div className="p-6">
                          <div className="flex items-center gap-2 mb-3 text-xs text-neutral-500">
                            <CalendarIcon className="w-3 h-3" />
                            <span>
                              {new Date(evt.date).toLocaleDateString(
                                locale === "fr" ? "fr-FR" : "en-GB"
                              )}
                            </span>
                          </div>

                          <h2 className="text-xl font-bold text-black dark:text-white mb-3 group-hover:text-[#BBD8E6] transition-colors">
                            {evt.title}
                          </h2>
                          <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-4 line-clamp-3">
                            {evt.description}
                          </p>

                          <div className="flex items-center justify-between">
                            <span className="text-xs text-neutral-500">{photoLabel}</span>
                            <Button variant="outline" size="sm">
                              {buttonLabel}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
